/**
 * Growth & Referral Sequence Templates
 * Based on best practices from Dropbox, Uber, Airbnb, PayPal
 *
 * Key insights from billion-dollar companies:
 * - Referral programs drove Dropbox from 100K to 4M users
 * - Timing is everything - ask after moments of delight
 * - Double-sided rewards outperform single-sided 2x
 * - Social proof + urgency = viral loops
 */

import { SequenceTemplate } from "./types";

export const REFERRAL_PROGRAM_LAUNCH: SequenceTemplate = {
  id: "referral-launch",
  name: "Referral Program Launch",
  type: "growth",
  description: "Launch and promote your referral program to existing users",
  strategy: "Dropbox grew 3900% using referrals. Target happy, active users. Offer double-sided rewards. Make sharing frictionless. This is your lowest CAC channel.",
  icon: "GiftOutlined",
  color: "#8b5cf6",
  expectedOutcomes: [
    "20-35% referral participation",
    "3-5x lower CAC than paid ads",
    "Viral coefficient > 1",
    "High-quality referred leads"
  ],
  bestPractices: [
    "Target users after success moments",
    "Offer rewards to BOTH referrer and referee",
    "Make sharing one-click easy",
    "Create unique referral links",
    "Show leaderboard for top referrers"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 30, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Referral Program Introduction",
      subject: "Give $25, Get $25 - Invite friends to {{product_name}}",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Introduce referral program with clear incentives",
      variables: [
        { key: "referrer_reward", defaultValue: "$25 credit" },
        { key: "referee_reward", defaultValue: "$25 off first month" },
        { key: "referral_link", defaultValue: "{{unique_referral_link}}" }
      ]
    },
    {
      name: "Referral Reminder",
      subject: "{{first_name}}, your friends are missing out!",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Reminder with social proof"
    },
    {
      name: "Referral Success Stories",
      subject: "How Sarah earned $500 in referral rewards",
      suggestedTemplateId: "product-feedback",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Inspire with referrer success stories"
    },
    {
      name: "Limited Time Bonus",
      subject: "This week only: 2X referral rewards!",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 14, unit: "days" },
      purpose: "Urgency boost for referral program"
    }
  ],
  tags: ["referral", "growth", "viral", "rewards"],
  bestFor: ["All SaaS products", "Consumer apps", "Viral products"],
  difficulty: "intermediate",
  setupTime: "20 minutes"
};

export const REVIEW_REQUEST: SequenceTemplate = {
  id: "review-request",
  name: "Review & Testimonial Request",
  type: "growth",
  description: "Collect reviews and testimonials from happy customers",
  strategy: "Ask for reviews after success moments - 70% of asked customers leave reviews. Reviews on G2/Capterra drive 30% of B2B SaaS signups. This is free, high-impact marketing.",
  icon: "StarOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "40-60% review completion rate",
    "Improved social proof",
    "Higher conversion rates",
    "SEO benefits from reviews"
  ],
  bestPractices: [
    "Time ask after measurable success",
    "Make review process 1-click easy",
    "Offer small incentive (not bribe)",
    "Follow up once, don't spam",
    "Respond to all reviews publicly"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 45, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Review Request",
      subject: "{{first_name}}, would you share your experience?",
      suggestedTemplateId: "product-survey",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Initial review request with easy link",
      variables: [
        { key: "review_platform", defaultValue: "G2" },
        { key: "review_link", defaultValue: "{{g2_review_link}}" },
        { key: "incentive", defaultValue: "$10 Amazon gift card" }
      ]
    },
    {
      name: "Review Reminder",
      subject: "Quick favor? 2 minutes to help other teams",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Gentle reminder with social angle"
    },
    {
      name: "Thank You + Testimonial",
      subject: "Thank you! One more thing...",
      suggestedTemplateId: "confirmation",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Thank reviewers + ask for detailed testimonial"
    }
  ],
  tags: ["reviews", "testimonials", "social-proof", "marketing"],
  bestFor: ["B2B SaaS", "Products on review platforms", "Growing companies"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const CASE_STUDY_INVITATION: SequenceTemplate = {
  id: "case-study",
  name: "Case Study Participation",
  type: "growth",
  description: "Invite successful customers to participate in case studies",
  strategy: "Case studies are the #1 B2B content for conversion. Target customers with impressive metrics. Offer co-marketing benefits. One great case study can generate 100+ leads.",
  icon: "FileTextOutlined",
  color: "#3b82f6",
  expectedOutcomes: [
    "25-40% participation rate",
    "High-converting sales content",
    "Deeper customer relationships",
    "Co-marketing opportunities"
  ],
  bestPractices: [
    "Target customers with measurable results",
    "Offer company/personal promotion",
    "Make participation easy (15-min interview)",
    "Share draft for approval",
    "Promote their brand alongside yours"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 90, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: ["Optimizer", "Wizard", "Maverick"],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Case Study Invitation",
      subject: "{{first_name}}, we'd love to feature {{company_name}}",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Invite to participate in case study",
      variables: [
        { key: "company_name", defaultValue: "{{user_company}}" },
        { key: "key_metric", defaultValue: "{{success_metric}}" },
        { key: "benefit", defaultValue: "Featured on our homepage" }
      ]
    },
    {
      name: "Case Study Benefits",
      subject: "What's in it for {{company_name}}?",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Explain co-marketing benefits"
    },
    {
      name: "Easy Process Explanation",
      subject: "15 minutes = Featured case study",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Make participation seem easy"
    }
  ],
  tags: ["case-study", "content", "marketing", "success"],
  bestFor: ["B2B SaaS", "Enterprise products", "Marketing teams"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const PARTNER_AFFILIATE_INVITATION: SequenceTemplate = {
  id: "partner-affiliate",
  name: "Partner/Affiliate Program Invitation",
  type: "growth",
  description: "Recruit power users as affiliates or partners",
  strategy: "Power users become your best salespeople. HubSpot's partner program generates 40% of revenue. Target consultants, agencies, and influencers in your user base.",
  icon: "TeamOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "10-15% partner conversion",
    "New revenue channel",
    "Extended market reach",
    "Brand ambassadors"
  ],
  bestPractices: [
    "Target users who already recommend you",
    "Offer competitive commissions (20-30%)",
    "Provide marketing materials",
    "Create partner portal/dashboard",
    "Offer tiered partnership levels"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 60, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: ["Optimizer", "Wizard", "Maverick"],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Partner Program Introduction",
      subject: "{{first_name}}, become a {{product_name}} partner",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Introduce partner/affiliate program",
      variables: [
        { key: "commission_rate", defaultValue: "25%" },
        { key: "partner_benefits", defaultValue: "Early access, co-marketing" }
      ]
    },
    {
      name: "Partner Success Stories",
      subject: "How partners earn $5K+/month with {{product_name}}",
      suggestedTemplateId: "product-feedback",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Show partner earning potential"
    },
    {
      name: "Partner Onboarding Call",
      subject: "Let's discuss your partnership opportunity",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Personal call to discuss partnership"
    }
  ],
  tags: ["partner", "affiliate", "revenue", "growth"],
  bestFor: ["B2B SaaS", "Products with reseller potential", "Agency tools"],
  difficulty: "advanced",
  setupTime: "20 minutes"
};

export const VIRAL_LOOP_ACTIVATION: SequenceTemplate = {
  id: "viral-loop",
  name: "Viral Loop Activation",
  type: "growth",
  description: "Encourage users to share and invite others",
  strategy: "Build sharing into the product experience. Calendly grew through meeting links. Loom grew through video shares. Find your natural viral moment and amplify it.",
  icon: "ShareAltOutlined",
  color: "#ec4899",
  expectedOutcomes: [
    "Viral coefficient > 0.5",
    "Organic user acquisition",
    "Network effects",
    "Community growth"
  ],
  bestPractices: [
    "Identify natural sharing moments",
    "Make sharing valuable to recipient",
    "Add branding to shared content",
    "Track viral metrics religiously",
    "Optimize share messaging"
  ],
  trigger: {
    eventType: "firstGeneration",
    delay: { value: 1, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Share Your Results",
      subject: "Your first AI generation is complete! Share your results",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Encourage sharing AI copilot results",
      variables: [
        { key: "copilot_name", defaultValue: "{{copilot_name}}" },
        { key: "share_link", defaultValue: "{{share_link}}" }
      ]
    },
    {
      name: "Showcase Results",
      subject: "Show off your AI copilot results",
      suggestedTemplateId: "monthly-analytics",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Encourage sharing success metrics"
    },
    {
      name: "Invite Colleagues",
      subject: "Invite your team to use AI copilots together",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Team invitation for viral growth"
    }
  ],
  tags: ["viral", "sharing", "growth", "organic"],
  bestFor: ["Products with shareable outputs", "Team tools", "Social products"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const COMMUNITY_BUILDING: SequenceTemplate = {
  id: "community-building",
  name: "Community Building Sequence",
  type: "growth",
  description: "Build and nurture your user community",
  strategy: "Communities increase retention by 25%+. Notion, Figma, and Webflow built empires through community. Invite users to join, participate, and contribute.",
  icon: "UsergroupAddOutlined",
  color: "#6366f1",
  expectedOutcomes: [
    "Active community members",
    "25%+ retention improvement",
    "User-generated content",
    "Product feedback loop"
  ],
  bestPractices: [
    "Start with exclusive invite-only",
    "Provide unique value in community",
    "Recognize and reward contributors",
    "Create regular community events",
    "Feature community success stories"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 7, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "medium"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Community Invitation",
      subject: "You're invited: Join the {{product_name}} community",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Exclusive community invitation",
      variables: [
        { key: "community_name", defaultValue: "Copilotly Community" },
        { key: "community_link", defaultValue: "{{slack_or_discord_link}}" },
        { key: "member_count", defaultValue: "{{community_size}}" }
      ]
    },
    {
      name: "Community Benefits",
      subject: "What you'll get in our community",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Showcase community value"
    },
    {
      name: "Community Event Invite",
      subject: "Join us: {{event_name}} this {{event_day}}",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Invite to community event"
    }
  ],
  tags: ["community", "engagement", "retention", "networking"],
  bestFor: ["Developer tools", "Creative tools", "Products with power users"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const GROWTH_TEMPLATES = [
  REFERRAL_PROGRAM_LAUNCH,
  REVIEW_REQUEST,
  CASE_STUDY_INVITATION,
  PARTNER_AFFILIATE_INVITATION,
  VIRAL_LOOP_ACTIVATION,
  COMMUNITY_BUILDING
];
